import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface DashboardKPIs {
  ca_mois: number;
  encours_clients: number;
  a_payer_fournisseurs: number;
  tresorerie_nette: number;
  nb_opportunites_open: number;
  ca_pondere_open: number;
  nb_sessions_a_venir: number;
}

export interface EvolutionCAPoint {
  mois: string;
  label: string;
  ca_mensuel: number;
  nb_factures: number;
}

const FIVE_MIN = 5 * 60 * 1000;

/** Fetch les KPI globaux du Tableau de bord — vue v_dashboard_kpis (security_invoker, RLS respectée) */
export function useDashboardKPIs() {
  return useQuery({
    queryKey: ['dashboard_kpis'],
    queryFn: async (): Promise<DashboardKPIs | null> => {
      const { data, error } = await supabase.from('v_dashboard_kpis').select('*').maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const r = data as Record<string, unknown>;
      return {
        ca_mois: Number(r.ca_mois ?? 0),
        encours_clients: Number(r.encours_clients ?? 0),
        a_payer_fournisseurs: Number(r.a_payer_fournisseurs ?? 0),
        tresorerie_nette: Number(r.tresorerie_nette ?? 0),
        nb_opportunites_open: Number(r.nb_opportunites_open ?? 0),
        ca_pondere_open: Number(r.ca_pondere_open ?? 0),
        nb_sessions_a_venir: Number(r.nb_sessions_a_venir ?? 0),
      };
    },
    staleTime: FIVE_MIN,
  });
}

/** 12 derniers mois — CA encaissé mensuel */
export function useEvolutionCA12Mois() {
  return useQuery({
    queryKey: ['evolution_ca_12mois'],
    queryFn: async (): Promise<EvolutionCAPoint[]> => {
      const { data, error } = await supabase
        .from('v_evolution_ca_mensuel')
        .select('*')
        .order('mois', { ascending: true });
      if (error) throw error;
      return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
        mois: String(r.mois),
        label: String(r.label ?? ''),
        ca_mensuel: Number(r.ca_mensuel ?? 0),
        nb_factures: Number(r.nb_factures ?? 0),
      }));
    },
    staleTime: FIVE_MIN,
  });
}
